import React, { useEffect, useRef, useState, useCallback } from 'react'
import { TrendingUp, TrendingDown, Minus, ChevronLeft, ChevronRight } from 'lucide-react'

/**
 * InsightCarousel — a scrollable set of "source-linked answer" cards that show the
 * KIND of intelligence a product gives (e.g. the Marketing Intelligence Brain).
 * Auto-advances, but is fully scrollable/controllable (drag, arrows, dots). Every
 * card is an ILLUSTRATIVE example, clearly labelled. Reduced-motion → no autoplay.
 *
 * Data (headless): product.insights = [{ q, a, sources:[], trend:'up'|'down'|'flat' }]
 */
const TREND = {
  up: { Icon: TrendingUp, cls: 'text-emerald-500' },
  down: { Icon: TrendingDown, cls: 'text-primary-600' },
  flat: { Icon: Minus, cls: 'text-gray-400' },
}

export default function InsightCarousel({ insights = [], eyebrow = 'Intelligence in action', title = 'The kind of answers you get' }) {
  const list = Array.isArray(insights) ? insights.filter((x) => x && x.q && x.a) : []
  const scRef = useRef(null)
  const [active, setActive] = useState(0)
  const [paused, setPaused] = useState(false)

  const scrollTo = useCallback((i) => {
    const el = scRef.current
    if (!el || !list.length) return
    const n = list.length
    const idx = ((i % n) + n) % n
    const card = el.children[idx]
    if (card) el.scrollTo({ left: card.offsetLeft - el.offsetLeft, behavior: 'smooth' })
    setActive(idx)
  }, [list.length])

  // Track the active card as the user scrolls.
  useEffect(() => {
    const el = scRef.current
    if (!el) return
    let raf
    const onScroll = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        const c = el.scrollLeft + el.clientWidth / 2
        let best = 0, bd = Infinity
        ;[...el.children].forEach((k, i) => {
          const mid = k.offsetLeft - el.offsetLeft + k.clientWidth / 2
          const d = Math.abs(mid - c)
          if (d < bd) { bd = d; best = i }
        })
        setActive(best)
      })
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => { el.removeEventListener('scroll', onScroll); cancelAnimationFrame(raf) }
  }, [])

  // Auto-advance (paused on hover/focus; off for reduced motion).
  useEffect(() => {
    if (list.length < 2 || paused) return
    if (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const id = setInterval(() => scrollTo(active + 1), 4500)
    return () => clearInterval(id)
  }, [active, paused, list.length, scrollTo])

  if (!list.length) return null

  return (
    <section className="bg-gray-50 border-b border-gray-200">
      <style>{`.rr-noscroll::-webkit-scrollbar{display:none}`}</style>
      <div className="max-w-6xl mx-auto px-5 py-20 sm:py-24">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.14em] text-primary-600">
              <span className="w-1.5 h-1.5 rounded-full bg-primary-600" aria-hidden="true" />{eyebrow}
            </p>
            <h2 className="text-3xl sm:text-4xl font-display font-black tracking-tight text-gray-900 mt-3" style={{ textWrap: 'balance' }}>{title}</h2>
            <p className="text-[15px] text-gray-500 mt-2">Illustrative examples · every answer traces back to the sources you connect.</p>
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <button onClick={() => scrollTo(active - 1)} aria-label="Previous example" className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:text-gray-900 hover:border-gray-400 transition-colors"><ChevronLeft size={18} /></button>
            <button onClick={() => scrollTo(active + 1)} aria-label="Next example" className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:text-gray-900 hover:border-gray-400 transition-colors"><ChevronRight size={18} /></button>
          </div>
        </div>

        <div
          ref={scRef}
          onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
          onFocusCapture={() => setPaused(true)} onBlurCapture={() => setPaused(false)}
          className="rr-noscroll mt-10 flex gap-5 overflow-x-auto snap-x snap-mandatory pb-2 -mx-5 px-5"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {list.map((it, i) => {
            const tr = TREND[it.trend] || TREND.flat
            const T = tr.Icon
            return (
              <article key={i} className="snap-start shrink-0 w-[86%] sm:w-[420px] bg-gray-100 border border-gray-200 rounded-2xl p-6 flex flex-col">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-gray-400">Example</span>
                  <span className={`inline-flex items-center ${tr.cls}`}><T size={19} strokeWidth={2.2} /></span>
                </div>
                <p className="text-[16px] font-bold text-gray-900 mt-3 leading-snug">{it.q}</p>
                <p className="text-[15px] text-gray-600 mt-2.5 leading-relaxed flex-1">{it.a}</p>
                <div className="flex flex-wrap gap-1.5 mt-4">
                  {(Array.isArray(it.sources) ? it.sources : []).map((s, si) => (
                    <span key={si} className="text-[11px] font-semibold text-primary-600 bg-primary-500/10 rounded-full px-2.5 py-1">{s}</span>
                  ))}
                </div>
                <p className="text-[11px] text-gray-400 mt-3">Source-linked answer</p>
              </article>
            )
          })}
        </div>

        <div className="flex items-center justify-center gap-2 mt-7">
          {list.map((_, i) => (
            <button key={i} onClick={() => scrollTo(i)} aria-label={`Go to example ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${i === active ? 'w-6 bg-primary-600' : 'w-1.5 bg-gray-300 hover:bg-gray-400'}`} />
          ))}
        </div>
      </div>
    </section>
  )
}
